'use client'

import { createContext, useContext, type ReactNode } from 'react'

/**
 * Carries the crawlable SSR "shadow" to a `next/dynamic` leaf's `loading` state.
 *
 * Why: after `HydrationGate` swaps to the client leaf, `next/dynamic` renders its
 * `loading` placeholder (as its own internal `<Suspense>` fallback) while the leaf's
 * chunk downloads — and `loading` can't receive the wrapper's props, so it can't
 * rebuild the props-aware shadow itself.
 * On a cold cache that left an empty gap (#372 r3459780556). We instead put the
 * already-rendered shadow element into context (a serializable element prop on the
 * provider), and the leaf's `loading` reads it — so the SAME shadow stays on
 * screen until the interactive chunk is ready. Only ONE subtree is ever mounted
 * (loading shadow OR the leaf), so there is no duplicate DOM / Radix / id clash.
 */
const IslandShadowContext = createContext<ReactNode>(null)

export function IslandShadowProvider({
  shadow,
  children,
}: {
  shadow: ReactNode
  children: ReactNode
}): ReactNode {
  return <IslandShadowContext.Provider value={shadow}>{children}</IslandShadowContext.Provider>
}

/** `next/dynamic` `loading` component: renders the shadow from context while the chunk loads. */
export function IslandShadowLoading(): ReactNode {
  return <>{useContext(IslandShadowContext)}</>
}

/**
 * Read the crawlable shadow from context (or `null` when there is no provider —
 * e.g. a `Safe*` leaf rendered standalone, outside `makeClientSSR`). Used by the
 * nested `createSafeMDXComponent` loader so its inner `next/dynamic` suspension
 * shows the SAME rich shadow instead of a generic skeleton when one is available
 * (#372 r3461700401), and falls back to the skeleton when it is not.
 */
export function useIslandShadow(): ReactNode {
  return useContext(IslandShadowContext)
}
